export const OPTION_KEYS = ['a', 'b', 'c', 'd', 'e'];
export const OPTION_LABELS = { a: 'ก', b: 'ข', c: 'ค', d: 'ง', e: 'จ' };
export const MAX_QUESTIONS = 50;
export const PAGE_SIZE = 10;
export const blankQuestion = () => ({ id: `q-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`, prompt: '', options: { a: '', b: '', c: '', d: '', e: '' }, correctOption: 'a', explanation: '' });
export function validTestId(id) { return typeof id === 'string' && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(id) && id.length <= 80 && !['new', 'result'].includes(id); }
export function cleanContent(draft) {
  return {
    title: String(draft.title || '').trim(), description: String(draft.description || '').trim(),
    questions: draft.questions.map(q => ({ id: q.id, prompt: q.prompt.trim(), options: Object.fromEntries(OPTION_KEYS.map(k => [k, (q.options[k] || '').trim()])), correctOption: q.correctOption, explanation: q.explanation.trim() })),
    scoreBands: draft.scoreBands.map(b => ({ min: b.min === '' ? NaN : Number(b.min), max: b.max === '' ? NaN : Number(b.max), description: b.description })).sort((a, b) => a.min - b.min),
  };
}
export function contentError(c) {
  const text = (s, max, required = true) => typeof s === 'string' && (!required || !!s.trim()) && s.length <= max;
  if (!c || !text(c.title, 200) || !text(c.description, 2000, false)) return 'กรุณากรอกชื่อไม่เกิน 200 ตัวอักษร และคำอธิบายไม่เกิน 2,000 ตัวอักษร';
  if (!Array.isArray(c.questions) || c.questions.length < 1 || c.questions.length > MAX_QUESTIONS) return 'แบบทดสอบต้องมี 1–50 ข้อ';
  const ids = new Set();
  for (const [i, q] of c.questions.entries()) {
    if (!q || !text(q.id, 80) || !/^[a-zA-Z0-9-]+$/.test(q.id) || ids.has(q.id)) return `รหัสข้อ ${i + 1} ไม่ถูกต้องหรือซ้ำกัน`;
    ids.add(q.id);
    if (!text(q.prompt, 2000) || !text(q.explanation, 4000)) return `ข้อ ${i + 1}: ต้องมีคำถาม (ไม่เกิน 2,000 ตัวอักษร) และคำอธิบายเฉลย (ไม่เกิน 4,000 ตัวอักษร)`;
    if (!q.options || !OPTION_KEYS.every(k => text(q.options[k] ?? '', 500, k !== 'e'))) return `ข้อ ${i + 1}: ก–ง ต้องมีข้อความ และตัวเลือกละไม่เกิน 500 ตัวอักษร`;
    if (!OPTION_KEYS.includes(q.correctOption) || !q.options[q.correctOption]?.trim()) return `ข้อ ${i + 1}: เฉลยต้องเป็นตัวเลือกที่มีข้อความ`;
  }
  if (!Array.isArray(c.scoreBands) || !c.scoreBands.length || c.scoreBands.length > c.questions.length + 1) return 'กรุณากำหนดช่วงคะแนนให้ครบ';
  let next = 0;
  for (const b of c.scoreBands) {
    if (!b || !Number.isInteger(b.min) || !Number.isInteger(b.max) || b.min !== next || b.max < b.min || b.max > c.questions.length || !text(b.description, 2000)) return 'ช่วงคะแนนต้องเป็นจำนวนเต็ม เริ่มที่ 0 ต่อเนื่อง ไม่ซ้อนกัน และมีคำอธิบาย';
    next = b.max + 1;
  }
  if (next !== c.questions.length + 1) return 'ช่วงคะแนนต้องครอบคลุม 0 ถึงคะแนนเต็ม';
  // Conservative UTF-8 payload cap, leaving room for Firestore field overhead and answers.
  if (unescape(encodeURIComponent(JSON.stringify(c))).length > 500 * 1024) return 'เนื้อหาข้อสอบมีขนาดใหญ่เกินไป กรุณาลดความยาวข้อความ';
  return '';
}
export function unansweredQuestions(content, answers) {
  return content.questions.filter(q => !OPTION_KEYS.includes(answers[q.id]) || !q.options[answers[q.id]]?.trim());
}
export function gradeTest(test, answers) {
  const error = contentError(test.content);
  if (error) throw new Error(error);
  if (unansweredQuestions(test.content, answers).length) throw new Error('กรุณาตอบให้ครบทุกข้อก่อนส่ง');
  return { testId: test.id, testVersion: test.version, courseId: test.courseId,
    testSnapshot: test.content, answers: Object.fromEntries(test.content.questions.map(q => [q.id, answers[q.id]])),
    score: test.content.questions.filter(q => answers[q.id] === q.correctOption).length,
    maxScore: test.content.questions.length, gradingMode: 'client' };
}
export function resultBand(result) { return result.testSnapshot.scoreBands.find(b => result.score >= b.min && result.score <= b.max); }
export function resultError(result) {
  if (!result || contentError(result.testSnapshot)) return 'ไม่สามารถแสดงกระดาษคำตอบนี้ได้';
  if (!result.answers || typeof result.answers !== 'object' || unansweredQuestions(result.testSnapshot, result.answers).length || !Number.isInteger(result.score) || result.score < 0 || result.score > result.testSnapshot.questions.length || result.maxScore !== result.testSnapshot.questions.length) return 'ข้อมูลผลแบบทดสอบไม่สมบูรณ์';
  return '';
}
export function pretestError(error) {
  const code = String(error?.code || '').replace('firestore/', '');
  if (['permission-denied', 'unauthenticated'].includes(code)) return 'ไม่มีสิทธิ์เข้าถึง หรือข้อสอบถูกแก้ไข/ปิดใช้งานแล้ว กรุณาตรวจสอบบัญชีและโหลดข้อสอบล่าสุด';
  if (code === 'failed-precondition') return 'ระบบยังไม่พร้อมสำหรับคำขอนี้ กรุณาลองใหม่ภายหลัง';
  if (['unavailable', 'deadline-exceeded'].includes(code)) return 'เชื่อมต่อไม่สำเร็จ กรุณาตรวจสอบอินเทอร์เน็ตแล้วลองอีกครั้ง';
  return error?.message || 'เกิดข้อผิดพลาด กรุณาลองอีกครั้ง';
}
export function pretestDate(value) {
  const date = value?.toDate ? value.toDate() : new Date(value);
  return !value || Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('th-TH', { timeZone: 'Asia/Bangkok', dateStyle: 'medium', timeStyle: 'short' });
}
